import { getIssueAnalyticsProps } from '@/core/modules/issues/analytics'
import { EIssueSeverity } from '@/core/modules/issues/constants'
import { formatDate } from '@/core/utils/time'
import {
    createColumnHelper,
    Row,
    SortingState,
    Table,
} from '@atbay/cyber-components'
import { useDrawer } from '@core/context/DrawerContext'
import { Analytics } from '@core/services/mixpanel'
import { getTableRowContext } from '@/ui/utils/tableUtils'
import { FC, useState } from 'react'
import { IssueStatus } from '@/ui/components/IssueStatus'
import TableEmpty from '../table/table-empty'
import {
    Cell,
    HeaderText,
    SenderCell,
    SubjectCell,
} from './FdIssuesTable.styles'
import { RelatedUsers } from './components/relatedUsers'
import { StyledCustomTable } from '../CustomTable/CustomTable.styles'
import Typography from '../typography'
import { useCustomTheme } from '@atbay/theming'
import SeverityIcon from '../svg/SeverityIcon'
import { SEVERITY_TEXT_MAPPING } from '@/ui/routes/digital-footprint/routes/assets/components/assetsTable/utils'
import { SeverityCell } from '@/ui/routes/digital-footprint/routes/assets/components/assetsTable/assetsTable.styles'

interface FdIssuesTableProps {
    issues: TFraudDefenseTableRow[]
}

const FdIssuesTable: FC<FdIssuesTableProps> = ({ issues }) => {
    const theme = useCustomTheme()
    const [sorting, setSorting] = useState<SortingState>([])
    const { setDrawerState, drawerState } = useDrawer()
    const { issues: sortedIssues, index: sortedIndex } =
        drawerState.contentProps
    const activeIssueId = drawerState.isDrawerOpen
        ? sortedIssues?.[sortedIndex]?.id
        : undefined

    const openDrawer = (
        sortedIssues: TFraudDefenseTableRow[],
        index: number
    ) => {
        setDrawerState((prevState) => ({
            ...prevState,
            isDrawerOpen: true,
            drawerContentType: 'fdIssueDetails',
            contentProps: { issues: sortedIssues, index },
        }))
    }

    const handleRowClick = (
        row: Row<TFraudDefenseTableRow>,
        table: Table<TFraudDefenseTableRow>
    ) => {
        const { sortedIndex, items } = getTableRowContext(row, table)
        Analytics.event('SEM::user_clicks_at_issue', {
            ...getIssueAnalyticsProps(row.original),
            origin: 'fd_issues_table',
        })

        openDrawer(items, sortedIndex)
    }

    const columnHelper = createColumnHelper<TFraudDefenseTableRow>()

    const columns = [
        {
            ...columnHelper.accessor('severity', {
                cell: (info) => (
                    <SeverityCell
                        onClick={() => handleRowClick(info.row, info.table)}
                        data-testid={`fd-table-severity-${info.cell.getValue().toLowerCase()}`}
                        className="table-cell-text"
                    >
                        <SeverityIcon severity={info.cell.getValue()} />
                        <Typography
                            className="table-cell-text"
                            variant="s-regular"
                            testId="highest-severity"
                        >
                            {SEVERITY_TEXT_MAPPING[info.cell.getValue()]}
                        </Typography>
                    </SeverityCell>
                ),
                header: () => (
                    <HeaderText
                        minWidth={'80px'}
                        variant="xs-regular"
                        className="table-cell-text"
                    >
                        Severity
                    </HeaderText>
                ),
                enableSorting: true,
                sortingFn: (a, b) => {
                    return (
                        EIssueSeverity[a.original.severity] -
                        EIssueSeverity[b.original.severity]
                    )
                },
            }),
            width: '12%',
        },
        {
            ...columnHelper.accessor('sender', {
                cell: (info) => (
                    <SenderCell
                        data-testid={'fd-table-sender'}
                        onClick={() => handleRowClick(info.row, info.table)}
                    >
                        <Typography
                            testId="fd-table-sender-name"
                            variant="xs-regular"
                            className="table-cell-text"
                        >
                            {info.cell.getValue().name || ''}
                        </Typography>
                        <Typography
                            testId={'fd-table-sender-email'}
                            variant="xs-regular"
                            className="table-cell-text"
                            color={theme.colors.greys[500]}
                        >
                            {info.cell.getValue().emailAddress || ''}
                        </Typography>
                    </SenderCell>
                ),
                header: () => (
                    <HeaderText
                        minWidth={'160px'}
                        variant="xs-regular"
                        className="table-cell-text"
                    >
                        Sender
                    </HeaderText>
                ),
            }),
            width: '16%',
        },
        {
            ...columnHelper.accessor('recipients', {
                cell: (info) => (
                    <Cell
                        className="table-cell-text"
                        data-testid="fd-table-recipients"
                        onClick={() => handleRowClick(info.row, info.table)}
                    >
                        <RelatedUsers
                            emails={info.cell
                                .getValue()
                                .map((email) => email?.emailAddress)}
                        />
                    </Cell>
                ),
                header: () => (
                    <HeaderText
                        variant="xs-regular"
                        className="table-cell-text"
                    >
                        Recipients
                    </HeaderText>
                ),
                enableSorting: false,
            }),
            width: '24%',
        },
        {
            ...columnHelper.accessor('status', {
                cell: (info) => (
                    <Cell
                        className="table-cell-text"
                        data-testid="fd-table-status"
                        onClick={() => handleRowClick(info.row, info.table)}
                        status={info.cell.getValue()}
                    >
                        <IssueStatus
                            status={info.cell.getValue()}
                            testId="fd-table-status-value"
                        />
                    </Cell>
                ),
                header: () => (
                    <HeaderText
                        variant="xs-regular"
                        className="table-cell-text"
                    >
                        Status
                    </HeaderText>
                ),
            }),
            width: '10%',
        },
        {
            ...columnHelper.accessor('emailTime', {
                cell: (info) => (
                    <Cell
                        className="table-cell-text"
                        data-testId={'fd-table-email-time'}
                        onClick={() => handleRowClick(info.row, info.table)}
                    >
                        {info.cell.getValue()
                            ? formatDate(
                                  info?.cell?.getValue(),
                                  'dd MMM yyyy HH:mm'
                              ) || '-'
                            : '-'}
                    </Cell>
                ),
                header: () => (
                    <HeaderText
                        variant="xs-regular"
                        className="table-cell-text"
                    >
                        Email time
                    </HeaderText>
                ),
                enableSorting: true,
            }),
            width: '14%',
        },
        {
            ...columnHelper.accessor('subject', {
                cell: (info) => (
                    <SubjectCell
                        data-testId={'fd-table-subject'}
                        onClick={() => handleRowClick(info.row, info.table)}
                    >
                        <Typography className="table-cell-text">
                            {info.cell.getValue() ? `"${info.cell.getValue()}"` : '-'}
                        </Typography>
                    </SubjectCell>
                ),
                header: () => (
                    <HeaderText
                        variant="xs-regular"
                        className="table-cell-text"
                    >
                        Subject
                    </HeaderText>
                ),
                enableSorting: false,
            }),
            width: '18%',
        },
    ]

    return (
        <StyledCustomTable
            activeRow={activeIssueId}
            getRowId={(row: TFraudDefenseTableRow) => row.id}
            columns={columns}
            data={issues}
            sorting={sorting}
            setSorting={setSorting}
            emptyState={<TableEmpty message="No open issues."></TableEmpty>}
        />
    )
}

export default FdIssuesTable
